import type { AppContextMenuProps } from './AppContextMenu.types';
import type { MenuV2 } from '@figma/fpl-components';
import type { PopoverOrigin } from '@mui/material';

type MenuV2Position = NonNullable<MenuV2.MenuV2RootProps['position']>;
type ResolvedAnchor = { element: HTMLElement | null; point: { x: number; y: number } | null };
type PositionArea = NonNullable<MenuV2Position['area']>;

/**
 * Translate the legacy MUI `anchorOrigin` / `transformOrigin` pair into a MenuV2
 * `position.area` (CSS `position-area`) keyword.
 *
 * MUI describes placement with two points — a point on the anchor (`anchorOrigin`)
 * and the point on the popup that snaps to it (`transformOrigin`). MenuV2 instead
 * names the region the popup occupies relative to the anchor.
 *
 * Opposite horizontal origins (anchor `right` ↔ popup `left`, or vice-versa) describe a
 * *side* flyout — the popup opens beside the anchor (submenus, the workspaces menu). Any
 * other pairing is a vertical placement (above / below), where the horizontal origins only
 * set edge alignment. FPL's own submenus use `right span-bottom` for a right, top-aligned
 * flyout, which is what we emit here.
 *
 * Returns `undefined` when neither origin is supplied so callers fall back to the
 * MenuV2 default (`bottom span-right`, i.e. the legacy default of opening down-right).
 */
export function mapOriginsToArea(
  anchorOrigin?: PopoverOrigin,
  transformOrigin?: PopoverOrigin,
): PositionArea | undefined {
  if (!anchorOrigin && !transformOrigin) return undefined;

  const anchorV = anchorOrigin?.vertical ?? 'bottom';
  const anchorH = anchorOrigin?.horizontal ?? 'left';
  const transformV = transformOrigin?.vertical ?? 'top';
  const transformH = transformOrigin?.horizontal ?? 'left';

  const isSideFlyout = (anchorH === 'right' && transformH === 'left') || (anchorH === 'left' && transformH === 'right');

  if (isSideFlyout) {
    // Popup sits to the anchor's right/left; the vertical span keeps its top (or bottom) edge
    // aligned with the anchor and grows away from it.
    const side: 'right' | 'left' = anchorH === 'right' ? 'right' : 'left';
    const verticalSpan: 'span-bottom' | 'span-top' | 'center' =
      anchorV === 'bottom' ? 'span-top' : anchorV === 'center' ? 'center' : 'span-bottom';
    return `${side} ${verticalSpan}` as PositionArea;
  }

  // Popup opens above the anchor when its bottom edge is pinned to the anchor's top.
  const vertical: 'top' | 'bottom' | undefined =
    anchorV === 'top' && transformV === 'bottom' ? 'top' : anchorV === 'center' ? undefined : 'bottom';

  // `span-right` keeps the popup's left edge aligned with the anchor and grows rightward;
  // `span-left` mirrors that for right-aligned menus.
  const horizontalSpan: 'span-right' | 'span-left' | 'center' =
    anchorH === 'right' ? 'span-left' : anchorH === 'center' ? 'center' : 'span-right';

  if (!vertical) return horizontalSpan;
  return `${vertical} ${horizontalSpan}` as PositionArea;
}

/** Parse a legacy MUI px offset string (e.g. `'4px'`, `'-8px'`) into a number; `undefined` if unset. */
export function parsePixelOffset(value: string | undefined): number | undefined {
  if (value == null) return undefined;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Build a MenuV2 `position` from the legacy MUI placement props: `anchorOrigin`/`transformOrigin`
 * become the `area` keyword and the px-string `offset` (`{ top, left }`) becomes the positioner
 * `margin` (`{ x, y }`) — `left` is the horizontal gap/skid, `top` the vertical one. Returns
 * `undefined` when nothing is specified so callers fall back to the MenuV2 default.
 */
export function computeMenuPosition(
  anchorOrigin?: PopoverOrigin,
  transformOrigin?: PopoverOrigin,
  offset?: { top?: string; left?: string },
): MenuV2Position | undefined {
  const area = mapOriginsToArea(anchorOrigin, transformOrigin);
  const x = parsePixelOffset(offset?.left);
  const y = parsePixelOffset(offset?.top);
  const margin = x != null && y != null ? { x, y } : x != null ? { x } : y != null ? { y } : undefined;
  if (!area && !margin) return undefined;
  return { ...(area && { area }), ...(margin && { margin }) };
}

/**
 * Resolve the legacy MUI `anchorEl` (element, virtual `getBoundingClientRect` object, or function)
 * plus optional mouse coordinates into either a DOM element anchor or a fixed point anchor.
 */
export function resolveAnchor(
  anchorEl: AppContextMenuProps['anchorEl'],
  mouseX?: number | null,
  mouseY?: number | null,
): ResolvedAnchor {
  const resolved = typeof anchorEl === 'function' ? anchorEl() : anchorEl;
  if (resolved instanceof HTMLElement) return { element: resolved, point: null };
  // Virtual anchor (a `getBoundingClientRect`-only object): anchor to its top-left point.
  if (resolved && typeof (resolved as { getBoundingClientRect?: unknown }).getBoundingClientRect === 'function') {
    const rect = (resolved as { getBoundingClientRect: () => DOMRect }).getBoundingClientRect();
    return { element: null, point: { x: rect.left, y: rect.top } };
  }
  if (mouseX != null && mouseY != null) return { element: null, point: { x: mouseX, y: mouseY } };
  return { element: null, point: null };
}
